// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from 'recharts';
import { formatCurrency, formatNumber } from '../utils/formatters';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { COLORS } from '../constants/colors';
import './Dashboard.css';

// Palette étendue basée sur les couleurs de la charte
const CHART_PALETTE = [
  COLORS.primary,
  COLORS.secondary,
  '#3EA436',   // vert (similaire au gradient)
  '#1DACCE',   // cyan
  '#FF6B6B',   // rouge doux
  '#A855F7',   // violet
  '#EC4899',   // rose
  '#F59E0B',   // ambre
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (!token) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id;

      const response = await fetch(`https://josephsbusiness.com/api/dashboard.php?user_id=${userId}`, {
        headers: {
          'X-User-ID': userId,
          'Accept': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/pages/signin2');
        return;
      }

      const result = await response.json();

      if (result.success) {
        setData(result);
      } else {
        setError(result.message || 'Erreur de chargement');
      }
    } catch (err) {
      console.error('Erreur dashboard:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: COLORS.lightGray, minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <div className="spinner-wrapper mb-4">
                <Spinner animation="border" variant="primary" className="pulse-spinner" style={{ width: '4rem', height: '4rem', color: COLORS.primary }} />
              </div>
              <h5 className="fw-bold" style={{ color: COLORS.primaryDark }}>Chargement du tableau de bord</h5>
              <p className="text-muted">Récupération des statistiques en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: COLORS.lightGray, minHeight: '100vh' }}>
          <div className="error-container text-center py-5">
            <div className="error-icon-wrapper mb-4">
              <i className="ri-error-warning-line text-danger" style={{ fontSize: '5rem' }}></i>
            </div>
            <h3 className="fw-bold text-danger mb-3">Oups ! Une erreur est survenue</h3>
            <p className="text-muted mb-4">{error}</p>
            <Button
              variant="primary"
              className="px-5 py-3 rounded-pill"
              onClick={fetchDashboardData}
              style={{ background: COLORS.primary, borderColor: COLORS.primary }}
            >
              <i className="ri-refresh-line me-2"></i>
              Réessayer
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: COLORS.lightGray, minHeight: '100vh' }}>
        <div className="container-fluid">

          {/* En‑tête avec onglets */}
          <Row className="mb-4 fade-in">
            <Col md={12}>
              <div className="d-flex flex-wrap justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-4">
                  <div className="page-icon-wrapper-modern" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <i className="ri-dashboard-line"></i>
                  </div>
                  <div>
                    <h1 className="display-6 fw-bold mb-2" style={{ color: COLORS.primaryDark }}>Tableau de bord</h1>
                    <p className="text-muted-600 mb-0 d-flex align-items-center">
                      <i className="ri-calendar-line me-2"></i>
                      {new Date().toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="mt-3 mt-md-0">
                  <div className="btn-group bg-light rounded-3 p-1">
                    {['general', 'finances', 'membres'].map((tab) => (
                      <Button
                        key={tab}
                        size="sm"
                        variant={activeTab === tab ? 'primary' : 'light'}
                        onClick={() => setActiveTab(tab)}
                        className="border-0"
                        style={activeTab === tab ? { background: COLORS.primary, color: '#fff' } : { color: COLORS.primaryDark }}
                      >
                        {tab === 'general' ? 'Général' : tab === 'finances' ? 'Finances' : 'Membres'}
                      </Button>
                    ))}
                  </div>
                  <OverlayTrigger placement="bottom" overlay={<Tooltip>Actualiser</Tooltip>}>
                    <Button
                      variant="outline-primary"
                      className="rounded-pill ms-3"
                      onClick={fetchDashboardData}
                      style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                    >
                      <i className="ri-refresh-line"></i>
                    </Button>
                  </OverlayTrigger>
                </div>
              </div>
            </Col>
          </Row>

          {/* SECTION GÉNÉRALE */}
          {activeTab === 'general' && (
            <>
              {/* Ligne 1 : Banque, Rétrogrades, Nouveaux */}
              <Row className="g-4 mb-4">
                <Col md={4}>
                  <div className="value-card-mini value-card-primary">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Total en Banque</span>
                      <h4 className="value-card-mini-amount">{formatCurrency(data?.total_banque || 0)}</h4>
                      <small className="text-muted">FCFA</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-bank-line"></i>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="value-card-mini value-card-danger">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Total Rétrogrades</span>
                      <h4 className="value-card-mini-amount">{formatNumber(data?.total_retrogrades || 0)}</h4>
                      <small className="text-muted">membres</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-user-unfollow-line"></i>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="value-card-mini value-card-success">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Total Nouveaux</span>
                      <h4 className="value-card-mini-amount">{formatNumber(data?.total_nouveaux || 0)}</h4>
                      <small className="text-muted">membres</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-user-add-line"></i>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Ligne 2 : Membres, Hommes, Femmes, Enfants */}
              <Row className="g-4 mb-4">
                <Col md={3}>
                  <div className="kpi-card-mini kpi-mini-primary">
                    <div className="kpi-mini-icon"><i className="ri-group-line"></i></div>
                    <div className="kpi-mini-content">
                      <span className="kpi-mini-label">Total Membres</span>
                      <h4 className="kpi-mini-value">{formatNumber(data?.total_membres || 0)}</h4>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="kpi-card-mini kpi-mini-info">
                    <div className="kpi-mini-icon"><i className="ri-user-line"></i></div>
                    <div className="kpi-mini-content">
                      <span className="kpi-mini-label">Hommes</span>
                      <h4 className="kpi-mini-value">{formatNumber(data?.total_hommes || 0)}</h4>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="kpi-card-mini kpi-mini-warning">
                    <div className="kpi-mini-icon"><i className="ri-user-line"></i></div>
                    <div className="kpi-mini-content">
                      <span className="kpi-mini-label">Femmes</span>
                      <h4 className="kpi-mini-value">{formatNumber(data?.total_femmes || 0)}</h4>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="kpi-card-mini kpi-mini-success">
                    <div className="kpi-mini-icon"><i className="ri-user-line"></i></div>
                    <div className="kpi-mini-content">
                      <span className="kpi-mini-label">Enfants</span>
                      <h4 className="kpi-mini-value">{formatNumber(data?.total_enfants || 0)}</h4>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Ligne 3 : Temples, Baptêmes eau, Baptêmes esprit */}
              <Row className="g-4 mb-4">
                <Col md={4}>
                  <div className="value-card-mini value-card-primary">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Total Temples</span>
                      <h4 className="value-card-mini-amount">{formatNumber(data?.total_temples || 0)}</h4>
                      <small className="text-muted">églises</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-home-line"></i>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="value-card-mini value-card-info">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Baptisés d'eau</span>
                      <h4 className="value-card-mini-amount">{formatNumber(data?.total_baptemes_eau || 0)}</h4>
                      <small className="text-muted">membres</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-drop-line"></i>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="value-card-mini value-card-warning">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Baptisés St-Esprit</span>
                      <h4 className="value-card-mini-amount">{formatNumber(data?.total_baptemes_esprit || 0)}</h4>
                      <small className="text-muted">membres</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-flashlight-line"></i>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Graphiques membres par église */}
              <Row className="g-4 mb-4">
                <Col lg={8}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Membres par église</h5>
                      <p className="text-muted-600 small mb-0">Répartition des membres par temple</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={data?.membres_par_eglise?.labels?.map((label, index) => ({
                          nom: label,
                          membres: data?.membres_par_eglise?.data[index] || 0
                        })) || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="nom" tick={{ fill: COLORS.textGray }} />
                          <YAxis tick={{ fill: COLORS.textGray }} />
                          <RechartsTooltip />
                          <Legend wrapperStyle={{ color: COLORS.textGray }} />
                          <Bar dataKey="membres" fill={COLORS.primary} name="Nombre de membres" radius={[4, 4, 0, 0]} stroke="none" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4 h-100">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Membres par église (%)</h5>
                      <p className="text-muted-600 small mb-0">Répartition en pourcentage</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={data?.membres_par_eglise?.labels?.map((label, index) => ({
                              name: label,
                              value: data?.membres_par_eglise?.data[index] || 0
                            })) || []}
                            cx="50%" cy="50%"
                            innerRadius={60} outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                            labelLine={false}
                            labelStyle={{ fill: COLORS.textGray, fontSize: '12px' }}
                          >
                            {(data?.membres_par_eglise?.data || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} stroke="none" />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {/* SECTION FINANCES */}
          {activeTab === 'finances' && (
            <>
              <Row className="g-4 mb-4">
                <Col md={4}>
                  <div className="value-card-mini value-card-success">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Total Revenus</span>
                      <h4 className="value-card-mini-amount">{formatCurrency(data?.total_revenus || 0)}</h4>
                      <small className="text-muted">FCFA</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-money-euro-circle-line"></i>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="value-card-mini value-card-secondary">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Caisse nationale</span>
                      <h4 className="value-card-mini-amount">{formatCurrency(data?.caisse_nationale || 0)}</h4>
                      <small className="text-muted">15% des revenus</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-bank-line"></i>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="value-card-mini value-card-danger">
                    <div className="value-card-mini-content">
                      <span className="value-card-mini-label">Total Dépenses</span>
                      <h4 className="value-card-mini-amount">{formatCurrency(data?.total_depenses || 0)}</h4>
                      <small className="text-muted">FCFA</small>
                    </div>
                    <div className="value-card-mini-icon">
                      <i className="ri-money-euro-circle-line"></i>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Graphique financier mensuel */}
              <Row className="g-4 mb-4">
                <Col lg={12}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Graphique financier mensuel</h5>
                      <p className="text-muted-600 small mb-0">Évolution des revenus et dépenses sur l'année</p>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <span className="badge bg-success-soft text-success px-3 py-2">
                          <i className="fa fa-circle me-2" style={{ color: '#3EA436' }}></i>Revenus
                        </span>
                        <span className="badge bg-warning-soft text-warning px-3 py-2">
                          <i className="fa fa-circle me-2" style={{ color: COLORS.secondary }}></i>Dépenses
                        </span>
                      </div>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={data?.graphique_financier?.mois?.map((mois, index) => ({
                          mois: mois,
                          revenus: data?.graphique_financier?.revenus[index] || 0,
                          depenses: data?.graphique_financier?.depenses[index] || 0
                        })) || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="mois" tick={{ fill: COLORS.textGray }} />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} tick={{ fill: COLORS.textGray }} />
                          <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                          <Legend wrapperStyle={{ color: COLORS.textGray }} />
                          <Bar dataKey="revenus" fill="#3EA436" name="Revenus" radius={[4, 4, 0, 0]} stroke="none" />
                          <Bar dataKey="depenses" fill={COLORS.secondary} name="Dépenses" radius={[4, 4, 0, 0]} stroke="none" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Entrées par catégorie */}
              <Row className="g-4 mb-4">
                <Col lg={8}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Entrées par catégorie</h5>
                      <p className="text-muted-600 small mb-0">Répartition des revenus par type</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={data?.entrees_par_categorie?.labels?.map((label, index) => ({
                          categorie: label,
                          montant: data?.entrees_par_categorie?.data[index] || 0
                        })) || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="categorie" tick={{ fill: COLORS.textGray }} />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} tick={{ fill: COLORS.textGray }} />
                          <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                          <Legend wrapperStyle={{ color: COLORS.textGray }} />
                          <Bar dataKey="montant" fill={COLORS.primary} name="Montant" radius={[4, 4, 0, 0]} stroke="none" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4 h-100">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Entrées par catégorie (%)</h5>
                      <p className="text-muted-600 small mb-0">Répartition en pourcentage</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={data?.entrees_par_categorie?.labels?.map((label, index) => ({
                              name: label,
                              value: data?.entrees_par_categorie?.data[index] || 0
                            })) || []}
                            cx="50%" cy="50%"
                            innerRadius={60} outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                            labelStyle={{ fill: COLORS.textGray, fontSize: '12px' }}
                          >
                            {(data?.entrees_par_categorie?.data || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} stroke="none" />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Entrées par église */}
              <Row className="g-4 mb-4">
                <Col lg={8}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Entrées par église</h5>
                      <p className="text-muted-600 small mb-0">Répartition des revenus par temple</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={data?.entrees_par_eglise?.labels?.map((label, index) => ({
                          eglise: label,
                          montant: data?.entrees_par_eglise?.data[index] || 0
                        })) || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="eglise" tick={{ fill: COLORS.textGray }} />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} tick={{ fill: COLORS.textGray }} />
                          <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                          <Legend wrapperStyle={{ color: COLORS.textGray }} />
                          <Bar dataKey="montant" fill="#3EA436" name="Montant" radius={[4, 4, 0, 0]} stroke="none" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4 h-100">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Entrées par église (%)</h5>
                      <p className="text-muted-600 small mb-0">Répartition en pourcentage</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={data?.entrees_par_eglise?.labels?.map((label, index) => ({
                              name: label,
                              value: data?.entrees_par_eglise?.data[index] || 0
                            })) || []}
                            cx="50%" cy="50%"
                            innerRadius={60} outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                            labelStyle={{ fill: COLORS.textGray, fontSize: '12px' }}
                          >
                            {(data?.entrees_par_eglise?.data || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} stroke="none" />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Dépenses par catégorie */}
              <Row className="g-4 mb-4">
                <Col lg={8}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Dépenses par catégorie</h5>
                      <p className="text-muted-600 small mb-0">Répartition des dépenses par type</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={data?.depenses_par_categorie?.labels?.map((label, index) => ({
                          categorie: label,
                          montant: data?.depenses_par_categorie?.data[index] || 0
                        })) || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="categorie" tick={{ fill: COLORS.textGray }} />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} tick={{ fill: COLORS.textGray }} />
                          <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                          <Legend wrapperStyle={{ color: COLORS.textGray }} />
                          <Bar dataKey="montant" fill={COLORS.secondary} name="Montant" radius={[4, 4, 0, 0]} stroke="none" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4 h-100">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Dépenses par catégorie (%)</h5>
                      <p className="text-muted-600 small mb-0">Répartition en pourcentage</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={data?.depenses_par_categorie?.labels?.map((label, index) => ({
                              name: label,
                              value: data?.depenses_par_categorie?.data[index] || 0
                            })) || []}
                            cx="50%" cy="50%"
                            innerRadius={60} outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                            labelStyle={{ fill: COLORS.textGray, fontSize: '12px' }}
                          >
                            {(data?.depenses_par_categorie?.data || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} stroke="none" />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Dépenses par église */}
              <Row className="g-4 mb-4">
                <Col lg={8}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Dépenses par église</h5>
                      <p className="text-muted-600 small mb-0">Répartition des dépenses par temple</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={data?.depenses_par_eglise?.labels?.map((label, index) => ({
                          eglise: label,
                          montant: data?.depenses_par_eglise?.data[index] || 0
                        })) || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="eglise" tick={{ fill: COLORS.textGray }} />
                          <YAxis tickFormatter={(value) => formatCurrency(value)} tick={{ fill: COLORS.textGray }} />
                          <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                          <Legend wrapperStyle={{ color: COLORS.textGray }} />
                          <Bar dataKey="montant" fill="#EF4444" name="Montant" radius={[4, 4, 0, 0]} stroke="none" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={4}>
                  <Card className="chart-card-modern border-0 shadow-sm rounded-4 h-100">
                    <Card.Header className="bg-transparent border-0 pt-4 px-4">
                      <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Dépenses par église (%)</h5>
                      <p className="text-muted-600 small mb-0">Répartition en pourcentage</p>
                    </Card.Header>
                    <Card.Body>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={data?.depenses_par_eglise?.labels?.map((label, index) => ({
                              name: label,
                              value: data?.depenses_par_eglise?.data[index] || 0
                            })) || []}
                            cx="50%" cy="50%"
                            innerRadius={60} outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                            labelStyle={{ fill: COLORS.textGray, fontSize: '12px' }}
                          >
                            {(data?.depenses_par_eglise?.data || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_PALETTE[index % CHART_PALETTE.length]} stroke="none" />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          {/* SECTION MEMBRES */}
          {activeTab === 'membres' && (
            <Row className="g-4 mb-4">
              <Col lg={12}>
                <Card className="chart-card-modern border-0 shadow-sm rounded-4">
                  <Card.Header className="bg-transparent border-0 pt-4 px-4">
                    <h5 className="mb-1 fw-bold" style={{ color: COLORS.primaryDark }}>Statistiques des membres</h5>
                    <p className="text-muted-600 small mb-0">Répartition par genre et rôle</p>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Hommes', value: data?.total_hommes || 0 },
                                { name: 'Femmes', value: data?.total_femmes || 0 },
                                { name: 'Enfants', value: data?.total_enfants || 0 }
                              ]}
                              cx="50%" cy="50%"
                              innerRadius={60} outerRadius={90}
                              paddingAngle={5}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                              labelStyle={{ fill: COLORS.textGray, fontSize: '12px' }}
                            >
                              <Cell fill={COLORS.primary} stroke="none" />
                              <Cell fill={COLORS.secondary} stroke="none" />
                              <Cell fill="#3EA436" stroke="none" />
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Col>
                      <Col md={6}>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Baptisés eau', value: data?.total_baptemes_eau || 0 },
                                { name: 'Baptisés esprit', value: data?.total_baptemes_esprit || 0 },
                                { name: 'Rétrogrades', value: data?.total_retrogrades || 0 },
                                { name: 'Nouveaux', value: data?.total_nouveaux || 0 }
                              ]}
                              cx="50%" cy="50%"
                              innerRadius={60} outerRadius={90}
                              paddingAngle={5}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                              labelStyle={{ fill: COLORS.textGray, fontSize: '12px' }}
                            >
                              <Cell fill={COLORS.primary} stroke="none" />
                              <Cell fill={COLORS.secondary} stroke="none" />
                              <Cell fill="#3EA436" stroke="none" />
                              <Cell fill="#EF4444" stroke="none" />
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Pied de page avec stats supplémentaires */}
          <Row className="mt-5">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-4 stats-footer-modern" style={{ background: COLORS.white }}>
                <Card.Body className="p-4">
                  <Row className="g-4">
                    <Col md={3}>
                      <div className="footer-stat-item text-center">
                        <div className="footer-stat-icon bg-primary-soft rounded-circle mx-auto mb-3" style={{ background: `${COLORS.primary}15` }}>
                          <i className="ri-home-line" style={{ color: COLORS.primary }}></i>
                        </div>
                        <h6 className="text-muted-600 small mb-2">Temples</h6>
                        <h4 className="fw-bold mb-1" style={{ color: COLORS.primary }}>{formatNumber(data?.total_temples || 0)}</h4>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="footer-stat-item text-center">
                        <div className="footer-stat-icon bg-success-soft rounded-circle mx-auto mb-3" style={{ background: '#3EA43615' }}>
                          <i className="ri-group-line" style={{ color: '#3EA436' }}></i>
                        </div>
                        <h6 className="text-muted-600 small mb-2">Membres</h6>
                        <h4 className="fw-bold mb-1" style={{ color: '#3EA436' }}>{formatNumber(data?.total_membres || 0)}</h4>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="footer-stat-item text-center">
                        <div className="footer-stat-icon bg-warning-soft rounded-circle mx-auto mb-3" style={{ background: `${COLORS.secondary}15` }}>
                          <i className="ri-money-euro-circle-line" style={{ color: COLORS.secondary }}></i>
                        </div>
                        <h6 className="text-muted-600 small mb-2">Revenus</h6>
                        <h4 className="fw-bold mb-1" style={{ color: COLORS.secondary }}>{formatCurrency(data?.total_revenus || 0)}</h4>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="footer-stat-item text-center">
                        <div className="footer-stat-icon bg-info-soft rounded-circle mx-auto mb-3" style={{ background: '#1DACCE15' }}>
                          <i className="ri-drop-line" style={{ color: '#1DACCE' }}></i>
                        </div>
                        <h6 className="text-muted-600 small mb-2">Baptêmes</h6>
                        <h4 className="fw-bold mb-1" style={{ color: '#1DACCE' }}>{formatNumber((data?.total_baptemes_eau || 0) + (data?.total_baptemes_esprit || 0))}</h4>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Styles globaux avec la charte */}
        <style>{`
          .dashboard-modern {
            background: ${COLORS.lightGray};
            min-height: 100vh;
          }

          .page-icon-wrapper-modern {
            width: 70px;
            height: 70px;
            border-radius: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 10px 20px rgba(40, 110, 182, 0.3);
          }
          .page-icon-wrapper-modern i {
            font-size: 36px;
            color: white;
          }

          /* Value cards */
          .value-card-mini {
            background: ${COLORS.white};
            border-radius: 0px;
            padding: 1rem 1.25rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            transition: all 0.2s ease;
            height: 100%;
            border-left: 3px solid;
          }
          .value-card-mini:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.08);
          }
          .value-card-mini-content { flex: 1; }
          .value-card-mini-label {
            font-size: 0.7rem;
            text-transform: uppercase;
            color: ${COLORS.textGray};
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .value-card-mini-amount {
            font-size: 1.35rem;
            font-weight: 700;
            margin: 0.1rem 0 0 0;
            color: ${COLORS.dark};
          }
          .value-card-mini-icon {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            margin-left: 0.75rem;
          }
          .value-card-mini-icon i { font-size: 1.4rem; }

          .value-card-primary { border-left-color: ${COLORS.primary}; }
          .value-card-primary .value-card-mini-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }

          .value-card-success { border-left-color: #3EA436; }
          .value-card-success .value-card-mini-icon { background: #3EA43615; color: #3EA436; }

          .value-card-danger { border-left-color: #EF4444; }
          .value-card-danger .value-card-mini-icon { background: #EF444415; color: #EF4444; }

          .value-card-warning { border-left-color: ${COLORS.secondary}; }
          .value-card-warning .value-card-mini-icon { background: ${COLORS.secondary}15; color: ${COLORS.secondary}; }

          .value-card-info { border-left-color: #1DACCE; }
          .value-card-info .value-card-mini-icon { background: #1DACCE15; color: #1DACCE; }

          .value-card-secondary { border-left-color: ${COLORS.secondary}; }
          .value-card-secondary .value-card-mini-icon { background: ${COLORS.secondary}15; color: ${COLORS.secondary}; }

          /* KPI mini */
          .kpi-card-mini {
            background: ${COLORS.white};
            border-radius: 0px;
            padding: 1rem;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            transition: all 0.2s ease;
            border-left: 3px solid;
            height: 100%;
          }
          .kpi-card-mini:hover {
            transform: translateY(-3px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.08);
          }
          .kpi-mini-icon {
            width: 44px;
            height: 44px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .kpi-mini-icon i { font-size: 1.4rem; }
          .kpi-mini-content { flex: 1; }
          .kpi-mini-label {
            font-size: 0.65rem;
            text-transform: uppercase;
            color: ${COLORS.textGray};
            font-weight: 600;
            letter-spacing: 0.3px;
          }
          .kpi-mini-value {
            font-size: 1.2rem;
            font-weight: 700;
            margin: 0.1rem 0 0 0;
            color: ${COLORS.dark};
          }

          .kpi-mini-primary { border-left-color: ${COLORS.primary}; }
          .kpi-mini-primary .kpi-mini-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }

          .kpi-mini-info { border-left-color: #1DACCE; }
          .kpi-mini-info .kpi-mini-icon { background: #1DACCE15; color: #1DACCE; }

          .kpi-mini-warning { border-left-color: ${COLORS.secondary}; }
          .kpi-mini-warning .kpi-mini-icon { background: ${COLORS.secondary}15; color: ${COLORS.secondary}; }

          .kpi-mini-success { border-left-color: #3EA436; }
          .kpi-mini-success .kpi-mini-icon { background: #3EA43615; color: #3EA436; }

          .kpi-mini-danger { border-left-color: #EF4444; }
          .kpi-mini-danger .kpi-mini-icon { background: #EF444415; color: #EF4444; }

          /* Chart cards */
          .chart-card-modern {
            border-radius: 0px !important;
            background: ${COLORS.white};
            transition: all 0.3s ease;
          }
          .chart-card-modern:hover {
            transform: translateY(-4px);
            box-shadow: 0 12px 30px rgba(0,0,0,0.06) !important;
          }

          .text-muted-600 { color: ${COLORS.textGray} !important; }

          .footer-stat-icon {
            width: 60px;
            height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            transition: all 0.3s ease;
          }
          .stats-footer-modern {
            border-radius: 0px !important;
          }

          .bg-success-soft { background: #3EA43615 !important; }
          .bg-warning-soft { background: ${COLORS.secondary}15 !important; }
          .bg-info-soft { background: #1DACCE15 !important; }
          .bg-primary-soft { background: ${COLORS.primary}15 !important; }

          .fade-in {
            animation: fadeIn 0.6s ease-out;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .btn-group .btn {
            padding: 0.5rem 1.5rem;
            font-weight: 500;
          }

          @media (max-width: 768px) {
            .value-card-mini, .kpi-card-mini { margin-bottom: 0.5rem; }
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
}